import React from "react";
import { Box, Divider, Typography, useMediaQuery } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { MicroRingChart } from "./MicroRingChart";
import { colorSystem } from "../../theme";
import { StackedMicroRing } from "./StackedMicroRing";
import useTheme from "@mui/styles/useTheme";
import { InternalNutritionEntryDto } from "../../utils/chart";
import dayjs from "dayjs";
import ClientNutritionTableRow from "./ClientNutritionTableRow";
import { thousandSeparator } from "./utils/thousandSeparator";
import clsx from "clsx";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    margin: theme.spacing(0),
    padding: theme.spacing(0, 2, 2, 2),

    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(0),
      margin: theme.spacing(-0.5, 0, 0, 0),
    },

    [theme.containerQueries.down("md")]: {
      padding: theme.spacing(0),
      margin: theme.spacing(-0.5, 0, 0, 0),
    },
  },

  table: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    overflow: "visible",
    maxWidth: "100%",
  },

  tableBody: {},

  tableRow: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    gap: theme.spacing(1),
    justifyContent: "space-between",
    width: "100%",

    padding: theme.spacing(2, 3, 2, 3),

    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(1, 0, 1, 2),
    },

    [theme.containerQueries.down("md")]: {
      padding: theme.spacing(1, 0, 1, 2),
    },
  },

  tableCell: {
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    borderBottom: "none",
    textWrap: "nowrap",
    width: "20%",

    [theme.breakpoints.down("sm")]: {
      width: "33%",
    },

    [theme.containerQueries.down("sm")]: {
      width: "33%",
    },
  },

  targetTableCell: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    borderBottom: "none",
    textWrap: "nowrap",
    width: "15%",

    [theme.breakpoints.down("md")]: {
      width: "20%",
    },

    [theme.containerQueries.down("md")]: {
      width: "20%",
    },
  },

  grayscale: {
    filter: "grayscale(100%)",
    pointerEvents: "none",
    cursor: "default",
  },
}));

const TableCellWithChart = ({
  children,
  value,
  color,
}: {
  children: React.ReactNode;
  value: number;
  color?: string;
}) => {
  const s = useStyles();

  return (
    <Box className={s.tableCell}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: -2 }}>
        <Box sx={{ width: "max-content" }}>
          <MicroRingChart value={value} color={color} />
        </Box>
        {children}
      </Box>
    </Box>
  );
};

interface ClientNutritionCardTableProps {
  data?: InternalNutritionEntryDto;
  showAverageIntake?: boolean;
  disableTags?: boolean;
}

export const ClientNutritionCardTable = (
  props: ClientNutritionCardTableProps,
) => {
  const { data, showAverageIntake, disableTags } = props;
  const s = useStyles();

  const mainSectionRows = [
    {
      title: "Protein",
      field: "protein",
      color: colorSystem.nutritionProteins,
      coeff: 4,
    },
    {
      title: "Carbs",
      field: "carbs",
      color: colorSystem.nutritionCarbs,
      coeff: 4,
    },
    { title: "Fat", field: "fat", color: colorSystem.nutritionFats, coeff: 9 },
  ];

  const calculatedCalories = mainSectionRows.map(
    (row) => data && data[row.field] * row.coeff,
  );

  return (
    <Box className={clsx(s.root, !data && s.grayscale)}>
      <Box className={s.table}>
        <Box className={s.tableRow}>
          <TableCellWithChart value={0}>
            <Typography variant="subtitle1" color="textSecondary">
              MACRO
            </Typography>
          </TableCellWithChart>
          <Box className={s.tableCell}>
            <Typography variant="subtitle1" color="textSecondary" align="right">
              {showAverageIntake ? "AVG. INTAKE" : "REMAINING"}
            </Typography>
          </Box>
          <Box className={s.targetTableCell}>
            <Typography align="right" variant="subtitle1" color="textSecondary">
              TARGET
            </Typography>
          </Box>
        </Box>
        <Divider sx={{ width: "100%", my: 1 }} />
        {mainSectionRows.map((row) => {
          const target =
            data &&
            data[
              `target${row.field[0].toUpperCase() + row.field.substring(1)}`
            ];

          return (
            <ClientNutritionTableRow
              key={row.field}
              value={data?.[row.field]}
              target={target}
              color={row.color}
              title={row.title}
              showAverageIntake={showAverageIntake}
            />
          );
        })}
        <Divider sx={{ width: "100%", my: 1 }} />
        <ClientNutritionTableRow
          value={data?.calories}
          target={data?.targetCalories}
          color={"textSecondary"}
          title={"Calories"}
          unit="kcal"
          showAverageIntake={showAverageIntake}
          chart={
            <StackedMicroRing
              values={calculatedCalories.map((calories, index) =>
                data && data.targetCalories
                  ? calories / data.targetCalories
                  : 0,
              )}
              colors={mainSectionRows.map((row) => row.color)}
            />
          }
          compactUnits
        />
      </Box>
    </Box>
  );
};
